<template>
  <div>
    <apexchart
      v-if="series.length"
      ref="chart"
      type="bar"
      :options="chartOptions"
      :series="series"
      :width="width"
    />
    <div v-else>No hay datos disponibles</div>
  </div>
</template>

<script>
export default {
  name: 'GenericBarWithGoals',
  props: {
    data: { type: Array, required: true },
    labelField: { type: String, default: 'key' },
    contractsField: { type: String, default: 'contracts' },
    amountField: { type: String, default: 'amount' },
    tenderField: { type: String, default: 'tenderAmount' },
    goalField: { type: String, default: 'bajaPct' },
    width: { type: String, default: '100%' },
    titleX: { type: String, default: '' },
  },
  data() {
    return {
      series: [],
      chartOptions: {
        chart: { stacked: false, toolbar: { show: true } },
        plotOptions: { bar: { horizontal: false, columnWidth: '45%' } },
        dataLabels: { enabled: false },
        colors: ['#4953CF', '#33C4C5', '#0B9B9C'],
        xaxis: { categories: [], labels: { show: false }, title: { text: '' } },
        yaxis: [
          {
            title: { text: 'Nº contratos' },
            labels: { formatter: v => Math.round(v) },
          },
          {
            opposite: true,
            title: { text: 'Importe (€)' },
            labels: {
              formatter(val) {
                if (val > 1e9) return `${(val / 1e9).toFixed(2)} B€`;
                if (val > 1e6) return `${(val / 1e6).toFixed(2)} M€`;
                return val.toLocaleString();
              },
            },
          },
        ],
        tooltip: { shared: true, intersect: false, position: 'top' },
        legend: { position: 'top' },
      },
    };
  },
  watch: {
    data: {
      handler() { this.buildChart(); },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    buildChart() {
      if (!this.data || !this.data.length) {
        this.series = [];
        return;
      }
      const validData = this.data.filter(d => d[this.labelField] != null
        && d[this.contractsField] != null
        && d[this.amountField] != null
        && d[this.tenderField] != null);

      const categories = validData.map(d => d[this.labelField]);
      const contracts = validData.map(d => Number(d[this.contractsField]) || 0);
      const totalAmount = validData.map(d => Number(d[this.amountField]) || 0);
      const adjudicado = validData.map(d => Number(d[this.tenderField]) || 0);

      this.series = [
        {
          name: 'Contratos', type: 'column', data: contracts,
        },
        {
          name: 'Importe total', type: 'column', data: totalAmount,
        },
        {
          name: 'Importe adjudicado', type: 'column', data: adjudicado,
        },
      ];

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: { ...this.chartOptions.xaxis, categories, title: { text: this.titleX } },
        yaxis: [
          {
            seriesName: 'Contratos',
            title: { text: 'Nº contratos' },
            labels: { formatter: v => Math.round(v) },
            decimalsInFloat: 0,
            forceNiceScale: true,
            tickAmount: 5,
            min: 0,
            max(max) { return Math.ceil(max / 10) * 10; },
          },
          {
            seriesName: 'Importe total',
            opposite: true,
            title: { text: 'Importe (€)' },
            labels: {
              formatter(val) {
                if (val > 1e9) return `${(val / 1e9).toFixed(2)} B€`;
                if (val > 1e6) return `${(val / 1e6).toFixed(2)} M€`;
                return `${new Intl.NumberFormat('es-ES').format(Math.round(val))} €`;
              },
            },
          },
          {
            seriesName: 'Importe adjudicado',
            show: false,
            opposite: true,
            labels: {
              formatter(val) {
                if (val > 1e9) return `${(val / 1e9).toFixed(2)} B€`;
                if (val > 1e6) return `${(val / 1e6).toFixed(2)} M€`;
                return `${new Intl.NumberFormat('es-ES').format(Math.round(val))} €`;
              },
            },
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          custom: ({ dataPointIndex }) => {
            const d = validData[dataPointIndex];
            const contractsNum = Number(d[this.contractsField]);
            const total = Number(d[this.amountField]).toLocaleString();
            const adj = Number(d[this.tenderField]).toLocaleString();
            let bajaText = '';

            if (d[this.goalField] != null && d[this.goalField].total_contracts > 0) {
              bajaText += `<div style="color:#FF8C00;font-weight:bold;margin-top:5px">Total contratos con baja: ${d[this.goalField].total_contracts} (${d[this.goalField].pct_contracts.toFixed(2)}%)</div> 
                <div style="color:#FF8C00;font-weight:bold;margin-top:5px">con una baja media del ${d[this.goalField].avg.toFixed(2)}%</div>`;
            }

            return `<div style="padding:12px;background:#fff;border:1px solid #e3e3e3;border-radius:4px;box-shadow:0 2px 4px rgba(0,0,0,0.1);font-family:Helvetica,Arial,sans-serif;font-size:14px">
                <div style="font-weight:600;margin-bottom:8px;color:#373d3f">${d[this.labelField]}</div>
                <div style="margin:4px 0;color:#6e8192">
                  <span style="display:inline-block;width:10px;height:10px;background:#4953CF;border-radius:2px;margin-right:6px"></span>
                  Contratos: <strong>${contractsNum}</strong>
                </div>
                <div style="margin:4px 0;color:#6e8192">
                  <span style="display:inline-block;width:10px;height:10px;background:#33C4C5;border-radius:2px;margin-right:6px"></span>
                  Importe total: <strong>${total} €</strong>
                </div>
                <div style="margin:4px 0;color:#6e8192">
                  <span style="display:inline-block;width:10px;height:10px;background:#0B9B9C;border-radius:2px;margin-right:6px"></span>
                  Importe adjudicado: <strong>${adj} €</strong>
                </div>
                ${bajaText}
              </div>
            `;
          },
        },
      };

      if (this.$refs.chart) {
        this.$refs.chart.updateOptions(this.chartOptions, false, true);
      }
    },
  },
};
</script>
